import { useEffect, useRef, useState, memo } from 'react'
import { Canvas, PencilBrush } from 'fabric'
import {
  Paintbrush,
  Eraser,
  Trash2,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

// ============================================================
// 类型定义
// ============================================================

export interface MaskPainterProps {
  open: boolean
  imageUrl: string
  initialPrompt?: string
  onConfirm: (data: { compositeBase64?: string; prompt: string; mode: 'inpaint' | 'redraw' }) => void
  onCancel: () => void
}

type BrushSize = 'small' | 'medium' | 'large'
type Tool = 'brush' | 'eraser'
type Mode = 'inpaint' | 'redraw'

const BRUSH_SIZES: Record<BrushSize, number> = {
  small: 15,
  medium: 42,  // 增加 20%
  large: 90,   // 增加 50%
}

// 画布内部真实分辨率（小红书标准 3:4）
const INTERNAL_WIDTH = 1080
const INTERNAL_HEIGHT = 1440

// ============================================================
// MaskPainter 组件
// ============================================================

export const MaskPainter = memo(function MaskPainter({
  open,
  imageUrl,
  initialPrompt = '',
  onConfirm,
  onCancel,
}: MaskPainterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fabricRef = useRef<Canvas | null>(null)
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const lastUrlRef = useRef<string>('')

  const [isLoading, setIsLoading] = useState(true)
  const [activeTool, setActiveTool] = useState<Tool>('brush')
  const activeToolRef = useRef<Tool>('brush')
  const [brushSize, setBrushSize] = useState<BrushSize>('medium')
  const brushSizeRef = useRef<BrushSize>('medium')
  const [hasDrawn, setHasDrawn] = useState(false)
  const [prompt, setPrompt] = useState(initialPrompt)
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0, scale: 1 })
  const [imageSrc, setImageSrc] = useState('')

  // 同步 brushSize 到 ref
  useEffect(() => {
    brushSizeRef.current = brushSize
  }, [brushSize])

  // 当弹窗打开或图片 URL 变化时，重置提示词
  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt)
    }
  }, [open, imageUrl])

  // 初始化画布（仅在打开时且图片变化时执行）
  useEffect(() => {
    if (!open) {
      if (fabricRef.current) {
        fabricRef.current.dispose()
        fabricRef.current = null
      }
      lastUrlRef.current = ''
      return
    }

    // 如果图片没变，不需要重新加载画布
    if (imageUrl === lastUrlRef.current) {
      return
    }
    lastUrlRef.current = imageUrl

    // 重置状态
    setHasDrawn(false)
    setIsLoading(true)
    
    // 添加时间戳强制绕过浏览器缓存，确保获取最新的 CORS 头
    const cacheBustedUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`
    setImageSrc(cacheBustedUrl)

    let mounted = true
    let retryCount = 0
    const maxRetries = 10

    const tryInit = () => {
      if (!mounted || !containerRef.current || !canvasRef.current) {
        if (mounted) setIsLoading(false)
        return
      }

      const containerWidth = containerRef.current.clientWidth - 40
      const containerHeight = containerRef.current.clientHeight - 40

      // 容器尺寸未准备好，延迟重试
      if (containerWidth <= 0 || containerHeight <= 0) {
        if (retryCount < maxRetries) {
          retryCount++
          setTimeout(tryInit, 100)
        } else {
          console.error('容器尺寸初始化超时')
          setIsLoading(false)
        }
        return
      }

      // 计算显示尺寸
      let displayWidth = containerWidth
      let displayHeight = (containerWidth * 4) / 3

      if (displayHeight > containerHeight) {
        displayHeight = containerHeight
        displayWidth = (containerHeight * 3) / 4
      }

      const scale = displayWidth / INTERNAL_WIDTH
      setDisplaySize({ width: displayWidth, height: displayHeight, scale })

      // 清理旧实例
      if (fabricRef.current) {
        fabricRef.current.dispose()
        fabricRef.current = null
      }

      // 创建画布
      const canvas = new Canvas(canvasRef.current, {
        width: displayWidth,
        height: displayHeight,
        isDrawingMode: true,
        backgroundColor: 'transparent',
      })

      canvas.setZoom(scale)
      fabricRef.current = canvas

      // 初始化画笔
      const brush = new PencilBrush(canvas)
      brush.width = BRUSH_SIZES[brushSizeRef.current]
      brush.color = 'rgba(255, 0, 0, 1)'
      canvas.freeDrawingBrush = brush

      // 路径创建事件
      canvas.on('path:created', (e: any) => {
        setHasDrawn(true)
        if (activeToolRef.current === 'eraser') {
          e.path.set({ globalCompositeOperation: 'destination-out' })
        } else {
          e.path.set({ globalCompositeOperation: 'source-over' })
        }
        canvas.renderAll()
      })

      // 鼠标移动更新光标
      canvas.on('mouse:move', (e: any) => {
        if (!cursorRef.current) return
        const pointer = canvas.getPointer(e.e)
        const zoom = canvas.getZoom()
        const size = BRUSH_SIZES[brushSizeRef.current] * zoom * 1.1

        cursorRef.current.style.left = `${pointer.x * zoom}px`
        cursorRef.current.style.top = `${pointer.y * zoom}px`
        cursorRef.current.style.width = `${size}px`
        cursorRef.current.style.height = `${size}px`
      })

      canvas.on('mouse:out', () => {
        if (cursorRef.current) cursorRef.current.style.display = 'none'
      })

      canvas.on('mouse:over', () => {
        if (cursorRef.current) cursorRef.current.style.display = 'block'
      })

      setIsLoading(false)
    }

    // 使用 requestAnimationFrame 确保 DOM 已渲染
    requestAnimationFrame(() => {
      if (mounted) {
        setTimeout(tryInit, 350)
      }
    })

    return () => {
      mounted = false
    }
  }, [open, imageUrl])

  // 监听画笔尺寸变化（不重新初始化画布）
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas || !canvas.freeDrawingBrush) return
    const brush = canvas.freeDrawingBrush as PencilBrush
    brush.width = BRUSH_SIZES[brushSize]
  }, [brushSize])

  // 监听工具切换
  useEffect(() => {
    activeToolRef.current = activeTool

    const canvas = fabricRef.current
    if (!canvas || !canvas.freeDrawingBrush) return

    const brush = canvas.freeDrawingBrush as any
    if (activeTool === 'eraser') {
      brush.color = 'rgba(255, 255, 255, 1)'
      brush.globalCompositeOperation = 'destination-out'
    } else {
      brush.color = 'rgba(255, 0, 0, 1)'
      brush.globalCompositeOperation = 'source-over'
    }
  }, [activeTool])

  const handleClear = () => {
    const canvas = fabricRef.current
    if (!canvas) return
    canvas.getObjects().forEach(obj => canvas.remove(obj))
    canvas.renderAll()
    setHasDrawn(false)
  }

  const handleConfirm = async () => {
    const currentMode: Mode = hasDrawn ? 'inpaint' : 'redraw'
    
    if (currentMode === 'redraw') {
      onConfirm({ prompt, mode: 'redraw' })
      return
    }

    const canvas = fabricRef.current
    if (!canvas) return

    // 创建合成画布
    if (!maskCanvasRef.current) {
      maskCanvasRef.current = document.createElement('canvas')
    }
    const compositeCanvas = maskCanvasRef.current
    compositeCanvas.width = INTERNAL_WIDTH
    compositeCanvas.height = INTERNAL_HEIGHT

    const ctx = compositeCanvas.getContext('2d')
    if (!ctx) return

    // 绘制原图（使用已经带时间戳的 URL，确保与显示的图片一致）
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = imageSrc

    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        ctx.drawImage(img, 0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT)
        resolve()
      }
      img.onerror = reject
    })

    // 在涂抹区域绘制纯白色
    canvas.getObjects().forEach(obj => {
      if (obj.type !== 'path') return
      const path = obj as any
      const isEraser = path.globalCompositeOperation === 'destination-out'

      if (!isEraser) {
        ctx.beginPath()
        ctx.lineWidth = path.strokeWidth || BRUSH_SIZES['medium']
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        const pathData = path.path
        if (!pathData) return
        pathData.forEach((segment: any[]) => {
          const command = segment[0]
          if (command === 'M') ctx.moveTo(segment[1], segment[2])
          else if (command === 'Q') ctx.quadraticCurveTo(segment[1], segment[2], segment[3], segment[4])
          else if (command === 'L') ctx.lineTo(segment[1], segment[2])
        })

        ctx.strokeStyle = '#ffffff'
        ctx.stroke()
      }
    })

    const compositeBase64 = compositeCanvas.toDataURL('image/png')
    onConfirm({ compositeBase64, prompt, mode: 'inpaint' })
  }

  return (
    <Dialog open={open} onOpenChange={val => !val && onCancel()}>
      <DialogContent
        className="max-w-[100vw] w-full h-[100vh] md:max-w-3xl md:h-[90vh] p-0 gap-0 overflow-hidden flex flex-col bg-zinc-950 border-none z-[100]"
        aria-describedby={undefined}
      >
        <DialogTitle className="px-4 py-3 border-b border-white/10 text-base font-medium flex items-center justify-between bg-zinc-900 text-white shrink-0 z-50">
          <div className="flex items-center gap-4">
              <span>AI 图片重绘</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </DialogTitle>

        <div className="flex-1 flex flex-col relative overflow-hidden bg-black">
            {/* 画布区域 */}
            <div 
                ref={containerRef}
                className="flex-1 relative flex items-center justify-center overflow-hidden bg-zinc-900"
            >
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-zinc-950 z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
                    </div>
                )}
                
                {/* 悬浮工具栏 */}
                {!isLoading && (
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 p-1.5 bg-zinc-900/90 backdrop-blur border border-white/10 rounded-full shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300">
                        <div className="flex items-center gap-1 border-r border-white/10 pr-2 mr-1">
                            <Button
                                variant={activeTool === 'brush' ? 'secondary' : 'ghost'}
                                size="icon"
                                className={cn("h-8 w-8 rounded-full", activeTool === 'brush' ? "bg-white text-black hover:bg-white/90" : "text-white/70 hover:bg-white/10 hover:text-white")}
                                onClick={() => setActiveTool('brush')}
                            >
                                <Paintbrush className="w-4 h-4" />
                            </Button>
                            <Button
                                variant={activeTool === 'eraser' ? 'secondary' : 'ghost'}
                                size="icon"
                                className={cn("h-8 w-8 rounded-full", activeTool === 'eraser' ? "bg-white text-black hover:bg-white/90" : "text-white/70 hover:bg-white/10 hover:text-white")}
                                onClick={() => setActiveTool('eraser')}
                            >
                                <Eraser className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="flex items-center gap-1.5 px-2">
                            {(['small', 'medium', 'large'] as BrushSize[]).map(size => (
                            <button
                                key={size}
                                className={cn(
                                'rounded-full border transition-all flex items-center justify-center',
                                brushSize === size
                                    ? 'border-white bg-white/20'
                                    : 'border-transparent hover:border-white/30'
                                )}
                                style={{ width: 32, height: 32 }}
                                onClick={() => setBrushSize(size)}
                            >
                                <div 
                                    className="bg-white rounded-full shadow-sm"
                                    style={{
                                        width: size === 'small' ? 6 : size === 'medium' ? 12 : 18,
                                        height: size === 'small' ? 6 : size === 'medium' ? 12 : 18,
                                    }}
                                />
                            </button>
                            ))}
                        </div>
                        
                        <div className="w-px h-4 bg-white/10 mx-1" />
                        
                         <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-full text-white/50 hover:text-red-400 hover:bg-white/10"
                            onClick={handleClear}
                            disabled={!hasDrawn}
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                )}

                <div 
                  className={cn("relative transition-opacity duration-500 flex items-center justify-center", isLoading ? 'opacity-0' : 'opacity-100')}
                  style={{ width: displaySize.width, height: displaySize.height }}
                >
                    {/* 背景图片层 */}
                    <img 
                      src={imageSrc} 
                      alt="background" 
                      crossOrigin="anonymous"
                      className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                    />

                    {/* Fabric 画布层 - 通过 opacity 实现统一透明度且不叠加 */}
                    <div className="absolute inset-0 z-10 opacity-50">
                      <canvas
                          ref={canvasRef}
                          className="block cursor-none"
                      />
                    </div>
                    
                    {/* 画笔光标 - 放在最上层 */}
                    {!isLoading && (
                        <div 
                            ref={cursorRef}
                            className="absolute pointer-events-none rounded-full border border-white shadow-[0_0_10px_rgba(0,0,0,0.5)] z-[100] -translate-x-1/2 -translate-y-1/2"
                            style={{ 
                                backgroundColor: activeTool === 'brush' ? 'rgba(255, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                                display: 'none'
                            }}
                        />
                    )}

                </div>
            </div>

            {/* 底部控制栏 */}
            <div className="shrink-0 bg-zinc-900/95 backdrop-blur-xl border-t border-white/10 z-50">
                <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-end">
                          <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                  <Label className="text-white/70 text-sm flex items-center gap-2">
                                      <Sparkles className="w-3.5 h-3.5 text-pink-400" />
                                      重绘要求
                                  </Label>
                                  <span className="text-[10px] text-white/30 uppercase tracking-widest">AI Generation Prompt</span>
                              </div>
                              <Textarea 
                                  placeholder="涂抹需要修改的地方，或直接描述你想要的新画面风格..."
                                  className="min-h-[80px] md:min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-white/20 focus-visible:ring-pink-500/50 resize-none text-base"
                                  value={prompt}
                                  onChange={(e) => setPrompt(e.target.value)}
                              />
                          </div>

                          <div className="flex flex-col gap-2 shrink-0">
                              <Button 
                                  className="h-12 md:h-full px-8 bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 text-white border-0 shadow-lg shadow-pink-500/20 font-bold min-w-[160px]"
                                  onClick={handleConfirm}
                                  disabled={isLoading || !prompt.trim()}
                              >
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  {hasDrawn ? '开始局部重绘' : '开始全图重绘'}
                              </Button>
                          </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-white/30">
                          <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                              {hasDrawn ? '已涂抹区域将被 AI 替换' : '整张图片将按指令重新生成'}
                          </div>
                          <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                              保持风格统一，生成自然
                          </div>
                      </div>
                  </div>
            </div>
        </div>
      </DialogContent>
    </Dialog>
  )
})
